import type { CreateWalletTopUpPayload } from "../types/walletTopUps.types";

/** A top-up sent but whose outcome is not known yet: kept until the backend says what became of it. */
export interface WalletTopUpAttempt {
  payload: CreateWalletTopUpPayload;
  /** For the notice; the wallet list may not be loaded when it shows. */
  currency: string | null;
}

export type WalletTopUpLookup = "not_placed" | "pending" | "unknown";

export const WALLET_TOP_UP_NOT_FOUND_CODE = "wallet_top_up_not_found";
export const WALLET_TOP_UP_IN_PROGRESS_CODE = "wallet_top_up_in_progress";

// No answer, a 5xx, a timeout or a throttle: the credit may have happened. Not a 409: every conflict the create
// answers (a wallet that is not active, a key reused with another body) is a refusal before anything is credited.
const AMBIGUOUS_STATUSES: ReadonlySet<number> = new Set([408, 429]);

/** After a failed create: true when it may still have credited, so the attempt must be kept. */
export function mayHaveCredited(status: number | null): boolean {
  return status === null || status >= 500 || AMBIGUOUS_STATUSES.has(status);
}

/** What a failed lookup by key means. Only these two codes are answers; anything else is not knowing yet. */
export function settleWalletTopUpLookup(status: number | null, code: string | null): WalletTopUpLookup {
  if (status === 404 && code === WALLET_TOP_UP_NOT_FOUND_CODE) return "not_placed";
  if (status === 409 && code === WALLET_TOP_UP_IN_PROGRESS_CODE) return "pending";
  return "unknown";
}

type TopUpBody = Omit<CreateWalletTopUpPayload, "idempotency_key">;

/** The same top-up as the stored one: retrying it must reuse the stored key, so the two can never both credit. */
export function isSameTopUp(stored: CreateWalletTopUpPayload, next: TopUpBody): boolean {
  return (
    stored.organization_id === next.organization_id &&
    stored.wallet_id === next.wallet_id &&
    stored.amount.trim() === next.amount.trim() &&
    stored.concept.trim() === next.concept.trim() &&
    stored.internal_note.trim() === next.internal_note.trim()
  );
}

/**
 * Right before a top-up leaves, against what the store holds NOW — another tab may have sent one to the same
 * organization while this challenge was open, and its answer may be lost. `null` refuses: a different top-up
 * is unresolved. Otherwise the payload to send; `adopted` when it is the unresolved top-up itself (same
 * body), sent with ITS key so the backend replays it instead of crediting a second time.
 */
export function reconcileTopUpAtSend(
  stored: WalletTopUpAttempt | null,
  outgoing: CreateWalletTopUpPayload,
): { payload: CreateWalletTopUpPayload; adopted: boolean } | null {
  if (!stored || stored.payload.idempotency_key === outgoing.idempotency_key) {
    return { payload: outgoing, adopted: false };
  }
  if (!isSameTopUp(stored.payload, outgoing)) return null;
  return { payload: stored.payload, adopted: true };
}

/** Refused before sending: another tab held the organization's top-up lock for too long. */
export class WalletTopUpBusyError extends Error {
  constructor() {
    super("Another tab is sending a top-up to this organization");
    this.name = "WalletTopUpBusyError";
  }
}

/** How long a send waits for another tab's top-up to the same organization to finish. */
const LOCK_WAIT_MS = 30_000;

type TopUpLocks = Pick<LockManager, "request">;

const browserLocks = (): TopUpLocks | null =>
  typeof navigator !== "undefined" && navigator.locks ? navigator.locks : null;

/**
 * Runs one send of a top-up holding the organization's lock, shared by every tab of this browser (Web Locks):
 * a second tab waits for the first request to settle, then reads the store again, so it can never send
 * another key while the first answer is still out. Waits at most 30 s (`WalletTopUpBusyError`); runs unlocked
 * where the browser has no lock manager.
 */
export async function withWalletTopUpLock<T>(
  organizationId: string,
  run: () => Promise<T>,
  locks: TopUpLocks | null = browserLocks(),
  waitMs: number = LOCK_WAIT_MS,
): Promise<T> {
  if (!locks) return run();
  const signal = typeof AbortSignal.timeout === "function" ? AbortSignal.timeout(waitMs) : undefined;
  // The timeout only bounds the wait: once the lock is held, the request decides, however long it takes.
  let acquired = false;
  try {
    return await locks.request(`coloca.walletTopUp:${organizationId}`, { signal }, () => {
      acquired = true;
      return run();
    });
  } catch (error: unknown) {
    if (!acquired && signal?.aborted) throw new WalletTopUpBusyError();
    throw error;
  }
}

type AttemptStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const STORAGE_KEY = "coloca.walletTopUp.unresolved";

const isAttempt = (value: unknown): value is WalletTopUpAttempt => {
  const attempt = value as Partial<WalletTopUpAttempt> | null;
  const payload = attempt?.payload;
  return (
    !!payload &&
    typeof payload.organization_id === "string" &&
    typeof payload.wallet_id === "string" &&
    typeof payload.amount === "string" &&
    typeof payload.concept === "string" &&
    typeof payload.internal_note === "string" &&
    typeof payload.idempotency_key === "string"
  );
};

/**
 * One unresolved attempt per organization. Survives closing the dialog and a reload: written before the
 * request leaves, removed only once its outcome is known. Falls back to memory when storage is refused.
 */
export function createUnresolvedTopUpStore(storage: AttemptStorage | null) {
  const memory = new Map<string, WalletTopUpAttempt>();

  const readAll = (): Record<string, unknown> => {
    try {
      const raw = storage?.getItem(STORAGE_KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  };

  const writeAll = (all: Record<string, unknown>) => {
    try {
      if (Object.keys(all).length) storage?.setItem(STORAGE_KEY, JSON.stringify(all));
      else storage?.removeItem(STORAGE_KEY);
    } catch {
      return;
    }
  };

  return {
    get(organizationId: string): WalletTopUpAttempt | null {
      const stored = readAll()[organizationId];
      return isAttempt(stored) ? stored : (memory.get(organizationId) ?? null);
    },
    /**
     * Removes the attempt only while it is still the one sent with `idempotencyKey`: another tab may have
     * stored its own since, and settling this one must never erase that one.
     */
    clear(organizationId: string, idempotencyKey: string): void {
      const all = readAll();
      const stored = all[organizationId];
      if (isAttempt(stored) && stored.payload.idempotency_key === idempotencyKey) {
        delete all[organizationId];
        writeAll(all);
      }
      if (memory.get(organizationId)?.payload.idempotency_key === idempotencyKey)
        memory.delete(organizationId);
    },
    /**
     * Calls `listener` when another tab of this browser changes the store (the `storage` event never fires in
     * the writer).
     */
    subscribe(listener: () => void): () => void {
      if (typeof window === "undefined") return () => undefined;
      const onStorage = (event: StorageEvent) => {
        if (event.key === STORAGE_KEY || event.key === null) listener();
      };
      window.addEventListener("storage", onStorage);
      return () => window.removeEventListener("storage", onStorage);
    },
    set(organizationId: string, attempt: WalletTopUpAttempt | null): void {
      const all = readAll();
      if (attempt) {
        memory.set(organizationId, attempt);
        all[organizationId] = attempt;
      } else {
        memory.delete(organizationId);
        delete all[organizationId];
      }
      writeAll(all);
    },
  };
}

const browserStorage = (): AttemptStorage | null => {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
};

export const unresolvedWalletTopUps = createUnresolvedTopUpStore(browserStorage());
